import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Container, Grid, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, useTheme, CircularProgress } from '@mui/material';

const LoginHistory = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ email: '', status: '', action: '', startDate: '', endDate: '' });

  const API_URL = process.env.REACT_APP_API_URL || '';

  const fetchData = async (pageArg = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageArg));
      params.set('limit', '25');
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });

      const res = await fetch(`${API_URL}/api/monitoring/login-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('novamatch_auth_token')}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      if (json.status === 'success') {
        setRows(json.data.rows || []);
        setPage(json.data.pagination.page);
        setPages(json.data.pagination.pages || 1);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => fetchData(1);
  const handleReset = () => {
    setFilters({ email: '', status: '', action: '', startDate: '', endDate: '' });
    fetchData(1);
  };

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        <Card>
          <CardHeader title="Login History" />
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField label="Email" size="small" fullWidth value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField label="Status" size="small" select fullWidth value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField label="Action" size="small" select fullWidth value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
                  <MenuItem value="">Login-related + Logout</MenuItem>
                  <MenuItem value="login_attempt">login_attempt</MenuItem>
                  <MenuItem value="login_success">login_success</MenuItem>
                  <MenuItem value="login_success_2fa_bypass">login_success_2fa_bypass</MenuItem>
                  <MenuItem value="login_failure">login_failure</MenuItem>
                  <MenuItem value="2fa_attempt">2fa_attempt</MenuItem>
                  <MenuItem value="2fa_success">2fa_success</MenuItem>
                  <MenuItem value="2fa_failure">2fa_failure</MenuItem>
                  <MenuItem value="logout">logout</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField label="Start date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField label="End date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <Button variant="contained" onClick={handleApply}>Apply</Button>
                  <Button variant="outlined" onClick={handleReset}>Reset</Button>
                </Box>
              </Grid>
            </Grid>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>User Agent</TableCell>
                      <TableCell>Failure Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(r.time).toLocaleString()}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.action}</TableCell>
                        <TableCell>{r.status}</TableCell>
                        <TableCell>{r.ip}</TableCell>
                        <TableCell>{r.location}</TableCell>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.userAgent}</TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.failureReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box display="flex" justifyContent="center" py={2}>
              <Pagination page={page} count={pages} onChange={(_, p) => fetchData(p)} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LoginHistory;


